import ArrowIcon from '~/assets/svg/newSvg/arrow-select.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-purple.svg?react';
import { SearchInput } from './SearchInput/SearchInput';
import styles from './EventUserSelect.module.scss';
import React, { useState } from 'react';
import Option from './Option/Option';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import LoaderSpinner from '~/components/Shared/LoaderSpinner/LoaderSpinner';

export interface IOption {
  id: number | string;
  name: string | JSX.Element;
  display_name?: string;
  full_name?: string;
  code?: string;
}

interface IMultiSelectProps {
  placeholder: string;
  value: IOption[];
  disabled: boolean | undefined;
  onDeleteClick: (option: IOption) => void;
}

const MultiSelect = ({
  value,
  placeholder,
  disabled,
  onDeleteClick,
}: IMultiSelectProps) => {
  if (value?.length) {
    return (
      <ul className={styles.multiSelect}>
        {value.map(value => {
          return (
            <li key={value.id} className={styles.selectedOption}>
              {value.full_name}/{value.code}
              <button
                onClick={
                  disabled
                    ? undefined
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDeleteClick(value);
                      }
                }
                type="button"
                className={styles.selectedOptionDelete}
              >
                <CloseIcon />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ISelectProps {
  placeholder: string;
  value: IOption | IOption[] | undefined;
}

const SingleSelect = ({ placeholder, value }: ISelectProps) => {
  if (!Array.isArray(value) && value?.name) {
    return (
      <span className={styles.value}>{value?.display_name || value?.name}</span>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ICustomSelectProps {
  label?: string;
  placeholder: string;
  options: IOption[];
  value?: IOption | IOption[] | any;
  disabled?: boolean;
  multiselect?: boolean;
  isSearchEnabled?: boolean;
  required?: boolean;
  isLoading?: boolean;
  error?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  className?: string;
  classNameLabel?: string;
  onChange: (option: IOption | IOption[]) => void;
  onTouch?: () => void;
}

const EventUserSelect = ({
  label,
  placeholder = 'Select option',
  options,
  value,
  isLoading,
  disabled,
  multiselect,
  isSearchEnabled,
  required,
  searchValue,
  onSearchChange,
  error,
  className,
  classNameLabel,
  onTouch,
  onChange,
}: ICustomSelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const handleToggleSelect = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option: IOption) => {
    if (multiselect && Array.isArray(value)) {
      const newValue = value.find((item: IOption) => item.id === option.id)
        ? value.filter((item: IOption) => item.id !== option.id)
        : [...value, option];
      onChange(newValue);
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleCloseDropdown = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  return (
    <div
      className={cn(
        styles.customSelect,
        multiselect ? styles.customSelectMultiselect : undefined,
        isOpen ? styles.customSelectOpen : undefined,
        disabled ? styles.customSelectDisabled : undefined,
        className
      )}
      onBlur={event => {
        handleCloseDropdown(event);
        if (onTouch) {
          onTouch();
        }
      }}
    >
      {label ? (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </span>
      ) : (
        ''
      )}
      <button
        type="button"
        onClick={disabled ? undefined : handleToggleSelect}
        className={cn(
          styles.customSelectLabel,
          isOpen ? styles.customSelectLabelOpen : undefined,
          classNameLabel
        )}
      >
        {multiselect && Array.isArray(value) ? (
          <MultiSelect
            value={value}
            onDeleteClick={handleSelectOption}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <SingleSelect placeholder={placeholder} value={value} />
        )}
        <ArrowIcon
          className={cn(
            styles.arrowIcon,
            isOpen ? styles.arrowIconOpen : undefined
          )}
        />
      </button>
      {isOpen ? (
        <ul
          style={isLoading ? { overflowY: 'hidden' } : {}}
          className={styles.options}
        >
          {isSearchEnabled && (
            <div className={styles.searchWrapper}>
              <SearchInput
                value={searchValue}
                onChange={e => {
                  onSearchChange(e.target.value);
                }}
                onClearPress={() => {
                  onSearchChange('');
                }}
              />
            </div>
          )}

          {options.map(option => (
            <Option
              key={option.id}
              onClick={() => handleSelectOption(option)}
              name={option.full_name + '/' + option.code}
              isSelected={
                Array.isArray(value)
                  ? !!value.find((item: IOption) => item.id === option.id)
                  : value?.id === option.id
              }
            />
          ))}
          {isLoading && (
            <div className={styles.loader}>
              <LoaderSpinner />
            </div>
          )}
          {options.length === 0 && !isLoading && (
            <span className={styles.noResults}>{t('nothing_found')}</span>
          )}
        </ul>
      ) : (
        ''
      )}
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default React.memo(EventUserSelect);
